import React from 'react'

import { Button, Space, Stack } from '@mantine/core'
import { useMutation, useQueryClient } from '@tanstack/react-query'

import { showAlert } from '#components/Modals'
import { STRIPE_TIER } from '#components/Settings/Org/plans'
import { useMst, WorkspaceOption } from '#state'
import { DatadogClient } from '#util/datadog'
import { extractTsRestSuccess } from '#util/extractTsRest'
import { WorkspaceDetailsPayload } from '#util/ScriptoApiClient/types'

import { ToggleSwitchForm } from './ToggleSwitchForm'

const ddLog = DatadogClient.getInstance()

interface PlanOptionToggleProps extends React.PropsWithChildren {
  option: WorkspaceOption
  workspace: WorkspaceDetailsPayload
}

const PlanOptionToggle = (props: PlanOptionToggleProps) => {
  const { scrapi } = useMst()
  const queryClient = useQueryClient()

  const option = props.workspace.options.find(
    ({ code }) => code === props.option,
  )

  const checked = Boolean(option?.enabled)

  const mutation = useMutation({
    async mutationFn(enabled: boolean) {
      const args = {
        params: { code: props.option, id: props.workspace.id },
        body: {},
      }

      const request = enabled
        ? scrapi.staff.workspacePlanOptionEnable(args)
        : scrapi.staff.workspacePlanOptionDisable(args)

      await extractTsRestSuccess(request, 200)

      return enabled
    },
    onSuccess(enabled) {
      queryClient.setQueryData(
        ['staff_org_info', props.workspace.id],
        (oldWorkspace: WorkspaceDetailsPayload) => {
          const code = props.option
          const options = oldWorkspace.options.map((option) =>
            option.code === code ? { ...option, enabled } : option,
          )

          // the initial server response sometimes omits disabled options
          // until we figure out why, we monkeypatch when needed
          if (!options.some((o) => o.code === props.option)) {
            options.push({ enabled, code, staffManaged: true })
          }

          return { ...oldWorkspace, options }
        },
      )
    },
    onError(e) {
      ddLog.error('error on calling API', undefined, e)
    },
  })

  return (
    <ToggleSwitchForm
      label={props.children}
      checked={checked}
      mutation={mutation}
      confirmDisableMessage={`Are you sure you want to disable ${JSON.stringify(props.children)} for this workspace?`}
    />
  )
}

const SyncPlanOptions = ({
  workspace,
}: {
  workspace: WorkspaceDetailsPayload
}) => {
  const { scrapi } = useMst()
  const queryClient = useQueryClient()

  const mutation = useMutation({
    async mutationFn() {
      await extractTsRestSuccess(
        scrapi.staff.workspaceBillingSync({
          params: { id: workspace.id },
          body: {},
        }),
        200,
      )
    },
    async onSuccess() {
      await queryClient.invalidateQueries({
        queryKey: ['staff_org_info', workspace.id],
      })
      showAlert({
        title: 'Success',
        children: 'Plan options synced from Stripe',
      })
    },
    onError(e) {
      ddLog.error('error on calling API', undefined, e)
      showAlert({
        title: 'Error',
        // oxlint-disable-next-line typescript/no-explicit-any
        children: (e as any).message,
      })
    },
  })

  return (
    <Button
      size="xs"
      variant="default"
      loading={mutation.isPending}
      onClick={() => mutation.mutate()}
    >
      Sync plan options
    </Button>
  )
}

export function PlanOptionsForm({
  workspace,
}: {
  workspace: WorkspaceDetailsPayload
}) {
  return (
    <>
      <Stack>
        <PlanOptionToggle workspace={workspace} option="PROMPTER_INTEGRATION">
          Prompter integration
        </PlanOptionToggle>
        <PlanOptionToggle workspace={workspace} option="PRIVATE_SCRIPTS">
          Private scripts
        </PlanOptionToggle>
        <PlanOptionToggle workspace={workspace} option="SCRIPT_LIMITING">
          Limited-access scripts
        </PlanOptionToggle>
        <PlanOptionToggle workspace={workspace} option="RUNDOWNS">
          Rundowns
        </PlanOptionToggle>
        <PlanOptionToggle workspace={workspace} option="PAGE_LOCKING">
          Page locking
        </PlanOptionToggle>
        <PlanOptionToggle workspace={workspace} option="CHANGE_TRACKING">
          Change tracking
        </PlanOptionToggle>
        <PlanOptionToggle workspace={workspace} option="REVISIONS">
          Revisions
        </PlanOptionToggle>
      </Stack>
      {workspace.tier === STRIPE_TIER ? (
        <>
          <Space h={10} />
          <SyncPlanOptions workspace={workspace} />
        </>
      ) : null}
    </>
  )
}
